import { useEffect, useState } from "react";
import { Youtube, Facebook, Tv, Play, ArrowLeft, Loader2, Eye, EyeOff, Bookmark, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DestinationTestResult } from "@/components/DestinationTestResult";
import api from "@/lib/api";
import { toast } from "sonner";

export const RumbleLogo = ({ className }) => (
  <span className={"inline-flex items-center justify-center rounded-md bg-[#85C742] " + className}><Play className="h-1/2 w-1/2 text-white translate-x-[1px]" fill="currentColor" /></span>
);

const TILES = [
  { key: "custom", name: "Custom RTMP", desc: "Send to any RTMP or RTMPS server", icon: <Tv className="h-7 w-7 text-amber-400" />, ring: "hover:border-amber-500/60" },
  { key: "youtube", name: "YouTube Live", desc: "Go live on your YouTube channel", icon: <Youtube className="h-8 w-8 text-red-500" />, ring: "hover:border-red-500/60" },
  { key: "rumble", name: "Rumble", desc: "Stream to your Rumble channel", icon: <RumbleLogo className="h-8 w-8" />, ring: "hover:border-[#85C742]/60" },
  { key: "facebook", name: "Facebook Live", desc: "Stream to your Page with a Live Producer key", icon: <Facebook className="h-8 w-8 text-blue-500" />, ring: "hover:border-blue-500/60" },
];
const FIXED = { youtube: "YouTube Studio → Go Live → Stream key", facebook: "Facebook Live Producer → Streaming software → Persistent stream key" };
const EMPTY = { name: "", rtmp_url: "", stream_key: "" };

function Tile({ t, onPick }) {
  return (
    <button type="button" data-testid={`dest-tile-${t.key}`} onClick={() => onPick(t.key)}
      className={`text-left rounded-xl border border-gray-800 bg-gray-950 p-4 flex flex-col gap-3 transition-[border-color,transform,background-color] cursor-pointer hover:-translate-y-0.5 hover:bg-gray-900 ${t.ring}`}>
      <div className="h-11 w-11 rounded-lg bg-gray-900 border border-gray-800 flex items-center justify-center">{t.icon}</div>
      <div>
        <p className="font-semibold text-white text-sm">{t.name}</p>
        <p className="text-xs text-gray-400 mt-0.5 leading-snug">{t.desc}</p>
      </div>
    </button>
  );
}

function SavedList({ templates, onUse, onRemove, busyId }) {
  if (!templates.length) return null;
  return (
    <div className="space-y-2" data-testid="dest-templates">
      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">My saved destinations</p>
      {templates.map((t) => {
        const tile = TILES.find((x) => x.key === t.platform) || TILES[0];
        return (
          <div key={t.id} data-testid={`dest-template-${t.id}`} className="flex items-center gap-3 rounded-lg border border-gray-800 bg-gray-950 px-3 py-2">
            <div className="h-8 w-8 rounded-md bg-gray-900 flex items-center justify-center [&_svg]:h-5 [&_svg]:w-5 [&>span]:h-5 [&>span]:w-5 shrink-0">{tile.icon}</div>
            <p className="text-sm text-white truncate flex-1">{t.name} <span className="text-gray-500 text-xs">· {tile.name}</span></p>
            <button data-testid={`dest-template-use-${t.id}`} onClick={() => onUse(t)} disabled={busyId === t.id} className="text-xs font-semibold gold-gradient text-black px-3 py-1.5 rounded-md disabled:opacity-60 inline-flex items-center gap-1">
              {busyId === t.id && <Loader2 className="h-3 w-3 animate-spin" />} Add
            </button>
            <button data-testid={`dest-template-remove-${t.id}`} title="Forget saved destination" onClick={() => onRemove(t)} className="h-7 w-7 rounded-md text-gray-500 hover:text-red-400 flex items-center justify-center"><X className="h-4 w-4" /></button>
          </div>
        );
      })}
    </div>
  );
}

function DestForm({ platform, form, setForm, editing, saveTpl, setSaveTpl, streamId }) {
  const [show, setShow] = useState(false);
  const fixed = FIXED[platform];
  const cls = "bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40";
  return (
    <div className="space-y-4 py-1">
      <div className="space-y-2">
        <Label className="text-gray-300">Name <span className="text-gray-500 font-normal">(optional)</span></Label>
        <Input data-testid="dest-name" value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={TILES.find((t) => t.key === platform)?.name} className={cls} />
      </div>
      {!fixed && (
        <div className="space-y-2">
          <Label className="text-gray-300">RTMP URL</Label>
          <Input data-testid="dest-rtmp-url" value={form.rtmp_url} onChange={(e) => setForm({ ...form, rtmp_url: e.target.value })} placeholder={platform === "rumble" ? "rtmp://… from Rumble Live Stream settings" : "rtmp:// or rtmps:// server URL"} className={cls + " font-mono text-sm"} />
        </div>
      )}
      <div className="space-y-2">
        <Label className="text-gray-300">Stream key</Label>
        <div className="relative">
          <Input data-testid="dest-stream-key" type={show ? "text" : "password"} value={form.stream_key} onChange={(e) => setForm({ ...form, stream_key: e.target.value })} placeholder="xxxx-xxxx-xxxx-xxxx" className={cls + " font-mono text-sm pr-10"} />
          <button type="button" data-testid="dest-key-visibility" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
        </div>
        {fixed && <p className="text-[11px] text-gray-500">Find it in {fixed}. The server address is filled in for you.</p>}
      </div>
      {!editing && (
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input data-testid="dest-save-template" type="checkbox" checked={saveTpl} onChange={(e) => setSaveTpl(e.target.checked)} className="h-4 w-4 accent-amber-500" />
          <Bookmark className="h-3.5 w-3.5 text-amber-400" /> Save to my destinations for one-click use on other streams
        </label>
      )}
      <DestinationTestResult streamId={streamId} draft={{ platform, ...form }} />
    </div>
  );
}

export function DestinationPicker({ open, onOpenChange, streamId, onSaved, editing }) {
  const [platform, setPlatform] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [saveTpl, setSaveTpl] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const tile = TILES.find((t) => t.key === platform);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setPlatform(TILES.some((t) => t.key === editing.platform) ? editing.platform : "custom");
      setForm({ name: editing.name || "", rtmp_url: editing.rtmp_url || "", stream_key: editing.stream_key || "" });
    } else {
      api.get(`/streams/${streamId}/destination-templates`).then((r) => setTemplates(r.data)).catch(() => {});
    }
  }, [open, editing, streamId]);

  const close = (v) => { onOpenChange(v); if (!v) { setPlatform(null); setForm(EMPTY); setSaveTpl(false); } };

  const useTemplate = async (t) => {
    setBusyId(t.id);
    try {
      const res = await api.post(`/streams/${streamId}/destinations`, { platform: t.platform, name: t.name, rtmp_url: t.rtmp_url, stream_key: t.stream_key });
      onSaved(res.data); toast.success(`${t.name} added`); close(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add destination"); } finally { setBusyId(null); }
  };

  const removeTemplate = async (t) => {
    try { await api.delete(`/streams/${streamId}/destination-templates/${t.id}`); setTemplates((l) => l.filter((x) => x.id !== t.id)); toast.success("Removed from saved destinations"); }
    catch { toast.error("Couldn't remove"); }
  };

  const save = async () => {
    if (!form.stream_key.trim() || (!FIXED[platform] && !form.rtmp_url.trim())) {
      toast.error(FIXED[platform] ? "Stream key is required" : "RTMP URL and stream key are required");
      return;
    }
    setSaving(true);
    try {
      const body = { platform, ...form };
      const res = editing
        ? await api.put(`/streams/${streamId}/destinations/${editing.id}`, body)
        : await api.post(`/streams/${streamId}/destinations`, body);
      if (saveTpl && !editing) await api.post(`/streams/${streamId}/destination-templates`, body).catch(() => toast.error("Saved to stream, but couldn't save to My destinations"));
      onSaved(res.data);
      toast.success(editing ? "Destination updated" : `${tile.name} destination added`);
      close(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save destination");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="destination-picker" className="bg-card border-gray-800 text-white max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            {platform && !editing && <button data-testid="dest-back" onClick={() => { setPlatform(null); setForm(EMPTY); }} className="h-7 w-7 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 flex items-center justify-center"><ArrowLeft className="h-4 w-4" /></button>}
            {editing ? `Edit ${tile?.name || "destination"}` : platform ? `Add ${tile.name}` : "Add Destination"}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {platform ? "We'll relay your broadcast here automatically whenever you go live." : "Choose where to stream. You can add as many destinations as you like."}
          </DialogDescription>
        </DialogHeader>
        {platform ? (
          <DestForm platform={platform} form={form} setForm={setForm} editing={!!editing} saveTpl={saveTpl} setSaveTpl={setSaveTpl} streamId={streamId} />
        ) : (
          <div className="space-y-5 py-1">
            <SavedList templates={templates} onUse={useTemplate} onRemove={removeTemplate} busyId={busyId} />
            <div data-testid="dest-tiles" className="grid grid-cols-2 gap-3">
              {TILES.map((t) => <Tile key={t.key} t={t} onPick={setPlatform} />)}
            </div>
          </div>
        )}
        {platform && (
          <DialogFooter>
            <button onClick={() => close(false)} className="px-4 py-2 rounded-lg text-gray-400 hover:text-white text-sm">Cancel</button>
            <button data-testid="submit-destination" onClick={save} disabled={saving} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Save destination"}
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
